import { FormStore, GlobalStore } from '@roc/feature-app-core';
import { action, flow, makeObservable, observable } from 'mobx';
import { VerifyBankInformationService } from '../services/verifyBankInformationService';

const verifyBankInformationForm = {
  fields: {
    accountName: {
      value: '',
      error: null,
      rule: 'required',
      message: 'The account name is required.',
    },
    routingNumber: {
      value: '',
      error: null,
      rule: 'required|regex:/^\\d{9}$/',
      message: 'The Routing number must be 9 digits long',
    },
    accountNumber: {
      value: '',
      error: null,
      rule: 'required|regex:/^\\d+$/',
      message: 'The account number is required',
    },
    bankName: {
      value: '',
      error: null,
      rule: 'required',
      message: 'The bank name is required.',
    },
    fullName: {
      value: '',
      error: null,
      rule: 'required',
      message: 'The name is required.',
    },
    phoneNumber: {
      value: '',
      error: null,
      rule: 'required|regex:/^\\d{3}-?\\d{3}-?\\d{4}$/|alpha_dash',
      message: 'phone must be a number with the format xxx-xxx-xxxx',
    },
    emailAddress: {
      value: '',
      error: null,
      rule: 'required|email',
      message: 'A valid email address is required.',
    },
    streetName: {
      value: '',
      error: null,
      rule: 'required',
      message: 'The street name is required.',
    },
    city: {
      value: '',
      error: null,
      rule: 'required',
      message: 'The city is required.',
    },
    zipCode: {
      value: '',
      error: null,
      rule: 'required|regex:/^\\d{5}$/',
      message: 'The zip code must be 5 digits.',
    },
    state: {
      value: '',
      error: null,
      rule: 'required',
      message: 'The state is required.',
    },
  },
  meta: {
    isValid: false,
    error: null,
  },
};

export class VerifyBankInformationStore extends FormStore {
  globalStore: GlobalStore;
  private verifyBankInformationService: VerifyBankInformationService;

  token: string;
  entityName: string;
  activeStep: number;
  loading: boolean;
  linkInvalid: boolean;
  completed: boolean;

  constructor(globalStore) {
    super({ ...verifyBankInformationForm }, globalStore);
    this.globalStore = globalStore;
    this.verifyBankInformationService = new VerifyBankInformationService();
    this.activeStep = 0;
    this.loading = false;
    this.linkInvalid = false;
    this.completed = false;
    this.entityName = '';
    this.token = '';

    makeObservable(this, {
      token: observable,
      entityName: observable,
      activeStep: observable,
      loading: observable,
      linkInvalid: observable,
      completed: observable,
      fetchSigningInfo: flow,
      submit: flow,
      goNextStep: action,
      goPrevStep: action,
    });
  }

  goNextStep() {
    this.activeStep++;
  }

  goPrevStep() {
    this.activeStep--;
  }

  *fetchSigningInfo(token: string) {
    this.loading = true;
    this.token = token;
    try {
      const response = yield this.verifyBankInformationService.getSigningInfo(token);
      const signingInfo = response?.data?.data;
      this.entityName = signingInfo?.entityName ?? '';
      this.completed = Boolean(signingInfo?.alreadySigned);
    } catch (error) {
      this.linkInvalid = true;
    } finally {
      this.loading = false;
    }
  }

  *submit(signature: string) {
    this.loading = true;
    try {
      const values = this.getFormValues();
      yield this.verifyBankInformationService.generateVerifyBankInformationDocument({
        token: this.token,
        accountName: values.accountName,
        routingNumber: values.routingNumber,
        accountNumber: values.accountNumber,
        bankName: values.bankName,
        fullName: values.fullName,
        phoneNumber: values.phoneNumber,
        emailAddress: values.emailAddress,
        streetName: values.streetName,
        city: values.city,
        zipCode: values.zipCode,
        state: values.state,
        signature,
        userAgent: navigator.userAgent,
      });
      this.completed = true;
    } catch (error) {
      console.error(error);
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error occurred while submitting your bank information. Please try again.',
      });
    } finally {
      this.loading = false;
    }
  }
}
